import { i18n } from '../i18n.js';
import { CardDrag } from './card-drag.js';
import { Effects } from './effects.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';

/**
 * CardPlay — what dragging a card MEANS, for the drag system to carry out.
 *
 * Owner decision, 2026-09-24 ("Moonfall Drag Play Brief"): dragging is the
 * only way to play a card. This module is the rulebook the gesture reads:
 *
 *  - which of the player's cards can be picked up at all (every one of them,
 *    whenever a match is being played);
 *  - why a card that is picked up cannot be played right now — shown on the
 *    card while it is held, and answered with the refusal cue if the player
 *    lets go where it would have played;
 *  - the two battlefield ZONES: a hand Character released anywhere above the
 *    hand is summoned into the slot the server will seat it in, and an
 *    untargeted Spell released there is cast.
 *
 * Every rule here is a client-side PREDICTION, never a permission. The drop
 * sends exactly the intent the retired click did, and the server's answer is
 * the only answer.
 */
let CardPlay = {};

CardPlay = (function () {
    'use strict';

    const BOARD_LIMIT = 5;
    /* How far above the resting hand the pointer must travel before a release
       plays the card, in card heights. A card nudged up out of the fan and let
       go is a player changing their mind, not a play. */
    const PLAY_LINE_CARD_HEIGHTS = 0.25;
    /* The summon clone is drawn to its landing slot when the pointer comes this
       close to the slot's centre, in card sizes. Farther away it stays under
       the pointer: a card that leapt to the leftmost slot from across the
       board would feel like it had been taken out of the player's hand. */
    const MAGNET_REACH = 0.75;
    /* A Spell spent by a drop plays its own commit where it was let go, and the
       server's surge starts there. The centred "Processing action…" chrome
       waits this long before it may appear, so it never lands on that moment
       and never flashes for a fast answer. The input lock is immediate. */
    const CAST_PROCESSING_GRACE_MS = 700;
    // Over the resting hand, down to this far above its top, is the card's home.
    const HOME_MARGIN_CARD_HEIGHTS = 0.1;

    function t(key, fallback) {
        try {
            const translated = i18n?.t?.(key);
            return translated && translated !== key ? translated : fallback;
        } catch (_) {
            return fallback;
        }
    }

    function state() {
        try {
            return (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : null;
        } catch (_) {
            return null;
        }
    }

    function isMatchInPlay(s) {
        if (!s || s.state !== 'playing') return false;
        if (document.body.classList.contains('mulligan-phase')) return false;
        const view = document.getElementById('game-view');
        return !!view && view.style.display !== 'none';
    }

    function handCardData(instanceId, s = state()) {
        const hand = s && s.player && Array.isArray(s.player.hand) ? s.player.hand : [];
        return hand.find((card) => card && String(card.instanceId) === String(instanceId)) || null;
    }

    function boardCardData(instanceId, s = state()) {
        const board = s && s.player && Array.isArray(s.player.summonedCards) ? s.player.summonedCards : [];
        return board.find((card) => card && String(card.instanceId) === String(instanceId)) || null;
    }

    function isTargetedSpell(card) {
        return !!card && card.type === 'Spell' && card.targetingType === 'Targetable';
    }

    function refusal(reason, key, fallback) {
        return { reason, message: t(key, fallback) };
    }

    /** Why this HAND card cannot be played right now, or null. */
    function handRefusal(card, s = state()) {
        if (!s || !card) return refusal('unknown', 'game.processingAction', 'Processing action...');
        if (!s.isYourTurn) return refusal('turn', 'game.notYourTurn', 'Not your turn');
        if (s.actionInProgress) return refusal('busy', 'game.processingAction', 'Processing action...');
        const mana = Number(s.player && s.player.mana);
        const cost = Number(card.manaCost);
        if (Number.isFinite(cost) && !(Number.isFinite(mana) && mana >= cost)) {
            return refusal('mana', 'game.notEnoughMana', 'Not enough mana');
        }
        if (card.type === 'Character') {
            const board = s.player && Array.isArray(s.player.summonedCards) ? s.player.summonedCards : [];
            if (board.length >= BOARD_LIMIT) return refusal('board-full', 'game.boardFull', 'Battlefield full');
        }
        return null;
    }

    /** Why this BOARD Character cannot act right now, or null. */
    function boardRefusal(card, s = state()) {
        if (!s || !card) return refusal('unknown', 'game.processingAction', 'Processing action...');
        if (!s.isYourTurn) return refusal('turn', 'game.notYourTurn', 'Not your turn');
        if (s.actionInProgress) return refusal('busy', 'game.processingAction', 'Processing action...');
        if (typeof GameState.canUseCard === 'function' && GameState.canUseCard(card.instanceId)) return null;
        const summoned = Array.isArray(s.summonedThisTurn) && s.summonedThisTurn.includes(card.instanceId);
        return summoned
            ? refusal('summoning-sickness', 'game.summoningSickness', 'Summoning sickness')
            : refusal('acted', 'game.alreadyActed', 'Already acted this turn');
    }

    /** The refusal cue: the card shakes where it came to rest, with the error sound. */
    function onRefusedDrop({ element, refusal: reason }) {
        try {
            Effects?.invalidAction?.({ cardElement: element || null, reason: reason && reason.reason });
        } catch (_) { /* feedback is presentation only */ }
    }

    /** CardDrag config for a card in the player's hand. */
    function handDragConfig(cardElement, card) {
        const instanceId = String(card.instanceId);
        return {
            instanceId,
            cardId: card.cardId || card.id || cardElement.dataset.cardId,
            sourceZone: 'player-hand',
            sourceType: 'hand',
            dropMode: isTargetedSpell(card) ? 'target' : 'zone',
            canDrag: () => isMatchInPlay(state()),
            refusal: () => {
                const s = state();
                return handRefusal(handCardData(instanceId, s) || card, s);
            },
            homeContains: (pointer) => overHand(pointer),
            homeTop: () => handLines().top,
            onRefusedDrop,
        };
    }

    /** CardDrag config for a Character on the player's board. */
    function boardDragConfig(card, slotIndex) {
        const instanceId = String(card.instanceId);
        return {
            instanceId,
            cardId: card.cardId || card.id,
            sourceZone: `player-board-slot-${slotIndex}`,
            sourceType: 'board',
            dropMode: 'target',
            canDrag: () => isMatchInPlay(state()),
            refusal: () => {
                const s = state();
                return boardRefusal(boardCardData(instanceId, s) || card, s);
            },
            onRefusedDrop,
        };
    }

    // --- The battlefield zones ---

    function restingHandTop() {
        let top = Infinity;
        document.querySelectorAll('#player-hand .card').forEach((node) => {
            const el = /** @type {HTMLElement} */ (node);
            if (el.classList.contains('dragging')) return;
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) top = Math.min(top, rect.top);
        });
        if (Number.isFinite(top)) return top;
        const hand = document.getElementById('player-hand');
        const rect = hand ? hand.getBoundingClientRect() : null;
        return rect && rect.height > 0 ? rect.top : document.documentElement.clientHeight * 0.8;
    }

    function cardHeight() {
        const probe = document.querySelector('#player-board .card-slot');
        const rect = probe ? probe.getBoundingClientRect() : null;
        return rect && rect.height > 0 ? rect.height : 140;
    }

    /* Asked on every frame of a drag. The hand and the board do not move under
       a held card, so the line is measured at most every few frames instead of
       forcing a layout read of every hand card each time. */
    const PLAY_LINE_TTL_MS = 200;
    let playLineCache = null;

    function handLines() {
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (playLineCache && now - playLineCache.at < PLAY_LINE_TTL_MS) return playLineCache;
        const top = restingHandTop();
        const height = cardHeight();
        playLineCache = {
            at: now,
            top,
            play: top - height * PLAY_LINE_CARD_HEIGHTS,
            home: top - height * HOME_MARGIN_CARD_HEIGHTS,
        };
        return playLineCache;
    }

    /** Above the hand, by enough that the release reads as deliberate. */
    function abovePlayLine(pointer) {
        return pointer.y < handLines().play;
    }

    /** Over the resting hand: a carried card is home here. */
    function overHand(pointer) {
        return pointer.y > handLines().home;
    }

    function draggedHandCard(data) {
        if (!data || data.sourceType !== 'hand') return null;
        return handCardData(data.instanceId);
    }

    /** The slot the server will seat the next summon in: the first free one. */
    function landingSlot() {
        const s = state();
        const board = s && s.player && Array.isArray(s.player.summonedCards) ? s.player.summonedCards : [];
        if (board.length >= BOARD_LIMIT) return null;
        return document.querySelector(`#player-board .card-slot[data-slot-index="${board.length}"]`);
    }

    function nearLandingSlot(pointer) {
        const slot = landingSlot();
        const rect = slot ? slot.getBoundingClientRect() : null;
        if (!rect || !(rect.width > 0)) return false;
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        return Math.abs(pointer.x - cx) <= rect.width * (0.5 + MAGNET_REACH)
            && Math.abs(pointer.y - cy) <= rect.height * (0.5 + MAGNET_REACH);
    }

    function summonZone() {
        return {
            zone: 'battlefield-summon',
            contains: (pointer) => abovePlayLine(pointer),
            accepts: (data) => {
                const card = draggedHandCard(data);
                return !!card && card.type === 'Character';
            },
            available: () => [landingSlot()].filter(Boolean),
            armed: () => [landingSlot()].filter(Boolean),
            anchor: () => landingSlot(),
            magnet: (pointer) => nearLandingSlot(pointer),
            release: 'hold',
            onDrop: (data) => SocketHandler.summonCharacter(data.instanceId),
        };
    }

    function castZone() {
        return {
            zone: 'battlefield-cast',
            contains: (pointer) => abovePlayLine(pointer),
            accepts: (data) => {
                const card = draggedHandCard(data);
                return !!card && card.type === 'Spell' && !isTargetedSpell(card);
            },
            /* No area is drawn for it (owner, 2026-09-25: "i do not like there
               being a drawn area where it needs to be dragged ... dragging
               anywhere will work"). A release anywhere above the hand casts;
               the carried card itself says so — its violet rim, the arm, the
               rune under the pointer. */
            anchor: () => null,
            magnet: () => false,
            release: 'consume',
            onDrop: (data) => {
                const result = SocketHandler.playAction(data.instanceId, null, null, undefined, {
                    processingGraceMs: CAST_PROCESSING_GRACE_MS,
                });
                if (result && typeof result.then === 'function') {
                    // SocketHandler renders the failure; the drop owns the promise
                    // so a refusal can never surface as an unhandled rejection.
                    return result.catch((error) => {
                        console.warn('[CardPlay] cast refused:', error && error.message);
                    });
                }
                return result;
            },
        };
    }

    /* Registered on the first render rather than at import: this module sits
       in the cards/board/card-drag import cycle, and touching CardDrag while
       that cycle is still evaluating would read it before it exists.
       Idempotent — a re-registration replaces the same two keys. */
    function registerZones() {
        if (typeof CardDrag?.registerZone !== 'function') return;
        CardDrag.registerZone('battlefield-summon', summonZone());
        CardDrag.registerZone('battlefield-cast', castZone());
    }

    return {
        BOARD_LIMIT,
        handRefusal,
        boardRefusal,
        handDragConfig,
        boardDragConfig,
        landingSlot,
        registerZones,
        isTargetedSpell,
    };
})();

export { CardPlay };
